import React, { useState } from 'react';
import Link from '../components/LocalizedLink';
import { MessageCircle, Mail, Phone, Send, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { FadeInUp, StaggerContainer, StaggerItem } from '../components/AnimatedSection';

const ContattiPage = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefono: '',
    tipoAttivita: '',
    messaggio: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.email) {
      toast.error(t('contatti.form.error_required'));
      return;
    }
    
    setIsSubmitting(true);

    try {
      await fetch(`/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: formData.nome,
          email: formData.email,
          tipo_attivita: formData.tipoAttivita || 'Non specificato',
          messaggio: `Tel: ${formData.telefono || 'Non fornito'}\n\n${formData.messaggio}`,
          lang: (i18n.language || 'it').split('-')[0]
        }),
      });

      const subject = encodeURIComponent(`[FaccioApp] Messaggio da ${formData.nome}`);
      const body = encodeURIComponent(
        `Ciao!\n\nMi chiamo ${formData.nome}.\n` +
        `${formData.tipoAttivita ? `Ho un/una ${formData.tipoAttivita}.\n` : ''}` +
        `${formData.telefono ? `Il mio numero è ${formData.telefono}.\n` : ''}` +
        `\n${formData.messaggio ? formData.messaggio : 'Vorrei saperne di più.'}\n\n` +
        `Email: ${formData.email}`
      );
      
      window.location.href = `mailto:info@faccioapp.it?subject=${subject}&body=${body}`;
      
      setIsSubmitted(true);
      toast.success(t('contatti.form.success_toast'));
      
      setTimeout(() => {
        setFormData({ nome: '', email: '', telefono: '', tipoAttivita: '', messaggio: '' });
        setIsSubmitted(false);
      }, 3000);

    } catch (error) {
      const subject = encodeURIComponent(`[FaccioApp] Messaggio da ${formData.nome}`);
      const body = encodeURIComponent(`${formData.messaggio || 'Vorrei info'}`);
      window.location.href = `mailto:info@faccioapp.it?subject=${subject}&body=${body}`;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-[var(--color-primary)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-60 h-60 bg-white/5 rounded-full" />
          <div className="absolute -bottom-10 -right-10 w-80 h-80 bg-white/5 rounded-full" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 relative z-10">
          <FadeInUp>
            <span className="text-white/80 uppercase text-sm font-semibold tracking-widest mb-4 inline-block">{t('contatti.hero_tag')}</span>
          </FadeInUp>
          <FadeInUp delay={0.1}>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('contatti.hero_title')}
            </h1>
          </FadeInUp>
          <FadeInUp delay={0.2}>
            <p className="text-xl md:text-2xl text-white/80">
              {t('contatti.hero_desc_1')}
              <strong className="text-white"> {t('contatti.hero_desc_2')}</strong>
            </p>
          </FadeInUp>
        </div>
      </section>

      {/* Contact Methods + Form */}
      <section className="py-16 md:py-20 bg-[var(--color-surface)]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Methods */}
            <div>
              <FadeInUp>
                <h2 className="text-xl font-bold text-[var(--color-text)] mb-6">
                  {t('contatti.direct_channels')}
                </h2>
              </FadeInUp>
              
              <StaggerContainer className="space-y-4" staggerDelay={0.1}>
                <StaggerItem>
                  <a 
                    href="https://wa.me/393534992310"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="card-elevated p-6 flex items-center gap-5 hover:border-emerald-300 transition-colors block"
                    data-testid="whatsapp-link"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                      <MessageCircle className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-bold text-[var(--color-text)] text-lg">{t('contatti.whatsapp')}</p>
                      <p className="text-[var(--color-text-muted)]">{t('contatti.whatsapp_desc')}</p>
                    </div>
                  </a>
                </StaggerItem>

                <StaggerItem>
                  <a 
                    href="tel:+393534992310"
                    className="card-elevated p-6 flex items-center gap-5 hover:border-sky-300 transition-colors block"
                    data-testid="phone-link"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center">
                      <Phone className="w-7 h-7 text-sky-600" />
                    </div>
                    <div>
                      <p className="font-bold text-[var(--color-text)] text-lg">{t('contatti.phone')}</p>
                      <p className="text-[var(--color-text-muted)]">{t('contatti.phone_desc')}</p>
                    </div>
                  </a>
                </StaggerItem>

                <StaggerItem>
                  <a 
                    href="mailto:info@faccioapp.it"
                    className="card-elevated p-6 flex items-center gap-5 hover:border-[var(--color-primary)]/50 transition-colors block"
                    data-testid="email-link"
                  >
                    <div className="w-14 h-14 rounded-2xl bg-[var(--color-primary)]/10 flex items-center justify-center">
                      <Mail className="w-7 h-7 text-[var(--color-primary)]" />
                    </div>
                    <div>
                      <p className="font-bold text-[var(--color-text)] text-lg">{t('contatti.email')}</p>
                      <p className="text-[var(--color-text-muted)]">info@faccioapp.it</p>
                    </div>
                  </a>
                </StaggerItem>
              </StaggerContainer>
            </div>

            {/* Form */}
            <FadeInUp delay={0.2}>
              <div>
                <h2 className="text-xl font-bold text-[var(--color-text)] mb-6">
                  {t('contatti.form_title')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome" className="text-[var(--color-text)]">{t('contatti.form.nome_label')}</Label>
                    <Input
                      id="nome"
                      data-testid="input-nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      placeholder={t('contatti.form.nome_placeholder')}
                      className="h-12 mt-2 rounded-xl"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefono" className="text-[var(--color-text)]">{t('contatti.form.telefono_label')}</Label>
                    <Input
                      id="telefono"
                      type="tel"
                      data-testid="input-telefono"
                      value={formData.telefono}
                      onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                      placeholder={t('contatti.form.telefono_placeholder')}
                      className="h-12 mt-2 rounded-xl"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-[var(--color-text)]">{t('contatti.form.email_label')}</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder={t('contatti.form.email_placeholder')}
                    className="h-12 mt-2 rounded-xl"
                    required
                  />
                </div>

                <div>
                  <Label className="text-[var(--color-text)]">{t('contatti.form.attivita_label')}</Label>
                  <Select
                    value={formData.tipoAttivita}
                    onValueChange={(value) => setFormData({...formData, tipoAttivita: value})}
                  >
                    <SelectTrigger data-testid="select-attivita" className="h-12 mt-2 rounded-xl">
                      <SelectValue placeholder={t('contatti.form.attivita_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ristorante_pizzeria_bar">{t('contatti.form.tipi.ristorante_pizzeria_bar')}</SelectItem>
                      <SelectItem value="hotel">{t('contatti.form.tipi.hotel')}</SelectItem>
                      <SelectItem value="negozio">{t('contatti.form.tipi.negozio')}</SelectItem>
                      <SelectItem value="agenzia_servizi">{t('contatti.form.tipi.agenzia_servizi')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="messaggio" className="text-[var(--color-text)]">{t('contatti.form.messaggio_label')}</Label>
                  <Textarea
                    id="messaggio"
                    data-testid="input-messaggio"
                    value={formData.messaggio}
                    onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                    placeholder={t('contatti.form.messaggio_placeholder')}
                    className="min-h-[120px] mt-2 rounded-xl"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || isSubmitted}
                  className="w-full h-14 btn-primary text-lg"
                  data-testid="submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('common.un_momento')}
                    </>
                  ) : isSubmitted ? (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      {t('common.fatto')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      {t('contatti.form.submit')}
                    </>
                  )}
                </Button>
              </form>
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContattiPage;
